<template>
  <div class="container">
    <section>
      <div class="layout">
        <div class="child">
          <div class="pure-g">
            <div class="pure-u-1">
              <div>
                <h4>{{titleText}}</h4>
              </div>
            </div>
          </div>
          <form class="pure-form pure-form-stacked" @submit.prevent="handleSubmit" novalidate>
            <fieldset>
              <div class="inner-form">
                <div class="pure-g">
                  <div class="pure-u-1">
                    <div class="input-container">
                      <input autofocus type="text" v-model="formModel.name" placeholder="Name*"
                             :class="nameValidationClassObj">
                    </div>
                  </div>
                  <div class="pure-u-1">
                    <div class="input-container">
                      <input type="text" v-model="formModel.email" placeholder="Email*"
                             :class="emailValidationClassObj">
                    </div>
                  </div>
                  <div class="pure-u-1">
                    <div class="password-container">
                      <input :type="passwordInputType" v-model="formModel.password" placeholder="Password*"
                             :class="passwordValidationClassObj">
                      <Fai v-if="showPassword" class="show-password-icon" v-on:click="setHidePassword" :icon="['far', 'eye']"/>
                      <Fai v-else class="show-password-icon" v-on:click="setShowPassword" :icon="['far', 'eye-slash']"/>
                    </div>
                  </div>
                  <div class="pure-u-1">
                    <div class="input-container">
                      <Datepicker :wrapper-class="datePickerWrapperClass"
                                  :input-class="birthDateValidationClassObj"
                                  placeholder="Birthdate*" :format="datePickerDateFormat"
                                  v-model="formModel.birthDate"></Datepicker>
                    </div>
                  </div>
                  <div class="pure-u-1">
                    <div class="input-container">
                      <select v-model="formModel.country" class="country-placeholder"
                              :class="countryClassObj" @click="onCountrySelectClick()"
                              @blur="onCountrySelectBlur()">
                        <option disabled value="">Country*</option>
                        <option :value="country" v-for="country in countryOptions">{{country}}</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div>
                  <div class="bigmi-radio-group">
                    <div class="radio-row">
                      <div class="radio-container">
                        <input type="radio" id="male" :value="radioValueMale" v-model="formModel.gender">
                      </div>
                      <label for="male">
                        <span>Male</span>
                      </label>
                    </div>
                    <div class="radio-row radio-row-last">
                      <div class="radio-container">
                        <input type="radio" id="female" :value="radioValueFemale" v-model="formModel.gender">
                      </div>
                      <label for="female">
                        <span>Female</span>
                      </label>
                    </div>
                  </div>
                  <div v-if="showGenderValidationUI">
                    <div class="gender-hint">
                      <small>Please choose a gender</small>
                    </div>
                  </div>
                </div>
                <div class="pdng-1">
                  <div>
                    <button type="submit" class="pure-button bigmi-wide-button bigmi-red-2-button">
                      <Fai v-if="isSignupButtonLoadingIndicatorShowing" :icon="['far', 'circle-notch']" spin/>
                      <span v-else>Submit</span>
                    </button>
                    <div v-if="signupFailed" class="sign-up-error">
                      {{signUpFailedErrorMessage}}
                    </div>
                  </div>
                </div>
                <div>
                  <div class="bigmi-checkbox">
                    <div class="input-container">
                      <input id="agree" type="checkbox" v-model="agreedToTerms">
                    </div>
                    <div>
                      <label for="agree">
                      <span>
                        I have read
                        <router-link :to="termsOfServiceRoute" target="_blank">
                          BimiLeap's terms of service.
                        </router-link>
                      </span>
                      </label>
                    </div>
                  </div>
                </div>
                <div v-if="showTermsOfServiceValidationUI">
                  <div class="terms-of-service-hint">
                    <small>Please confirm you read the Terms of Service</small>
                  </div>
                </div>
              </div>
            </fieldset>
          </form>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
  import {DatepickerAsync} from './views/DatePickerAsync'
  import {mapGetters} from 'vuex'
  import {
    GENDER_FEMALE,
    GENDER_MALE,
    CSS_CLASS_NAME_INVALID,
    CSS_CLASS_NAME_DATEPICKER_INPUT,
    SIGNUP,
    LOGIN,
    DATE_FORMAT_DATEPICKER,
    GET_IS_SIGNUP_PROCESS_LOADING,
    R_NAME_DASHBOARD, R_NAME_PRIVACY, LOADING_COMPLETED_SUCCESS, LOADING_COMPLETED_FAILURE,
  } from '../services/constants'

  const signUpFormModelFactory = () => {
    return {
      name: '',
      email: '',
      password: '',
      birthDate: '',
      country: '',
      gender: '',
    }
  }
  const CSS_CLASS_COUNTRY_SELECTED = 'country-selected'
  export default {
    name: 'SignUp',
    components: {
      'Datepicker': DatepickerAsync,
    },
    data() {
      return {
        formModel: signUpFormModelFactory(),
        agreedToTerms: false,
        submitTouched: false,
        countrySelectExpanded: false,
        showPassword: false,
      }
    },
    computed: {
      ...mapGetters([
        GET_IS_SIGNUP_PROCESS_LOADING,
      ]),
      titleText() {
        return `SIGN UP`
      },
      isNameValid() {
        return this.formModel.name.length > 2
      },
      showNameValidationUI() {
        return !this.isNameValid && this.submitTouched
      },
      nameValidationClassObj() {
        return {
          [CSS_CLASS_NAME_INVALID]: this.showNameValidationUI
        }
      },
      isEmailValid() {
        return this.formModel.email.length > 3
          && this.formModel.email.includes('@')
          && this.formModel.email.includes('.')
      },
      showEmailValidationUI() {
        return !this.isEmailValid && this.submitTouched
      },
      emailValidationClassObj() {
        return {
          [CSS_CLASS_NAME_INVALID]: this.showEmailValidationUI
        }
      },
      isPasswordValid() {
        return this.formModel.password.length > 5
      },
      passwordInputType(){
        return this.showPassword ? "text" : "password"
      },
      showPasswordValidationUI() {
        return !this.isPasswordValid && this.submitTouched
      },
      passwordValidationClassObj() {
        return {
          [CSS_CLASS_NAME_INVALID]: this.showPasswordValidationUI
        }
      },
      isCountryValid() {
        return this.countryOptions.some(countryName => {
          return countryName === this.formModel.country
        })
      },
      showCountryValidationUI() {
        return !this.isCountryValid && this.submitTouched
      },
      countryValidationClassObj() {
        return {
          [CSS_CLASS_NAME_INVALID]: this.showCountryValidationUI
        }
      },
      isBirthDateValid() {
        return !!this.formModel.birthDate
      },
      showBirthDateValidationUI() {
        return !this.isBirthDateValid && this.submitTouched
      },
      birthDateValidationClassObj() {
        return {
          [CSS_CLASS_NAME_INVALID]: this.showBirthDateValidationUI
        }
      },
      isGenderValid() {
        return this.formModel.gender === GENDER_MALE
          || this.formModel.gender === GENDER_FEMALE
      },
      showGenderValidationUI() {
        return !this.isGenderValid && this.submitTouched
      },
      isTermsOfServiceValid() {
        return this.agreedToTerms
      },
      showTermsOfServiceValidationUI() {
        return !this.isTermsOfServiceValid && this.submitTouched
      },
      isFormModelValid() {
        return this.isNameValid
          && this.isEmailValid
          && this.isPasswordValid
          && this.isBirthDateValid
          && this.isGenderValid
          && this.agreedToTerms
      },
      outgoingSignupModel() {
        return {
          username: this.formModel.name,
          email: this.formModel.email,
          password: this.formModel.password,
          birthDay: this.formModel.birthDate,
          country: this.formModel.country,
          gender: this.formModel.gender === GENDER_MALE ? 1 : 2,
          id: 0,
          status: 0,
          subscribed: false,
        }
      },
      outgoingLoginModel() {
        return {
          email: this.outgoingSignupModel.email,
          password: this.outgoingSignupModel.password,
        }
      },
      radioValueMale() {
        return GENDER_MALE
      },
      radioValueFemale() {
        return GENDER_FEMALE
      },
      isSignupButtonLoadingIndicatorShowing() {
        return this[GET_IS_SIGNUP_PROCESS_LOADING]
      },
      countryOptions() {
        return this.$store.state.dataModule.countries.data.map(countryobj => countryobj.name)
      },
      countrySelectClassObj() {
        return {
          [CSS_CLASS_COUNTRY_SELECTED]: !!this.formModel.country || this.countrySelectExpanded
        }
      },
      countryClassObj() {
        return Object.assign({}, this.countrySelectClassObj, this.countryValidationClassObj)
      },
      datePickerWrapperClass() {
        return CSS_CLASS_NAME_DATEPICKER_INPUT
      },
      datePickerDateFormat() {
        return DATE_FORMAT_DATEPICKER
      },
      homeRoute() {
        return {
          name: R_NAME_DASHBOARD
        }
      },
      termsOfServiceRoute(){
        return{
          name: R_NAME_PRIVACY
        }
      },
      isSignUpSuccessful(){
        return this.$store.state.userModule.signupState.loadingState === LOADING_COMPLETED_SUCCESS
      },
      signUpFailedErrorMessage(){
        return this.$store.state.userModule.signupState.error
      },
      signupFailed(){
        return this.$store.state.userModule.signupState.loadingState === LOADING_COMPLETED_FAILURE
      }

    },
    methods: {
      handleSubmit() {
        this.submitTouched = true
        if (this.isFormModelValid) {
          this.$store.dispatch(SIGNUP, this.outgoingSignupModel)
            .then(() => {
              if(this.isSignUpSuccessful){
                this.$store.dispatch(LOGIN, this.outgoingLoginModel)
                  .then(() => {
                    this.$router.push(this.homeRoute)
                  })
              }
            })

        }
      },
      resetFormModel() {
        this.formModel = signUpFormModelFactory()
        this.submitTouched = false
        this.agreedToTerms = false
        this.countrySelectExpanded = false
      },
      onTouched() {
        this.submitTouched = true
      },
      onCountrySelectClick() {
        this.countrySelectExpanded = true
      },
      onCountrySelectBlur() {
        this.countrySelectExpanded = false
      },
      setHidePassword(){
        this.showPassword = false
      },
      setShowPassword(){
        this.showPassword = true
      },
    },
    created() {
    },
    mounted() {
    }
  }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
  @import "../scss/main";

  .container {
    height: 100%;
  }

  .password-container{
    display: flex;
    align-items: center;

    .show-password-icon{
      margin: 0.25em 0 0.25em 0.5em;
      font-size: medium;
      cursor: pointer;
    }
  }

  section {
    height: 100%;
  }

  .layout {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100%;
  }

  .child {
    width: 100%;
    max-width: 500px;
    margin: 1em;
  }

  h4 {
    text-align: center;
    color: $red-2;
  }

  h5 {
    color: $cyan-blue-3;
  }

  .inner-form {
    background-color: $gray-1;
    padding: 1em;
  }

  .pure-form input[type="text"], .pure-form textarea, .pure-form input[type="password"] {
    box-shadow: none;
    width: 100%;
    color: $cyan-blue-3;
    border-color: $cyan-blue-3;

    &:focus {
      border-color: $cyan-blue-1;
    }

    &:placeholder-shown {
      color: $gray-3;
    }

    &:-ms-input-placeholder {
      color: $gray-3;
    }

    &.invalid {
      border-color: $red-2;
    }
  }

  .pure-form {
    select {
      border-color: $cyan-blue-3;

      &:focus {
        border-color: $cyan-blue-1;
      }
    }
  }

  .question-title {
    font-size: 0.83em;
    font-weight: bold;
    color: $cyan-blue-3;
  }

  .bigmi-radio-group {
    display: flex;
    align-items: center;

    .radio-row {
      margin: 0;

      &:not(.radio-row-last) {
      }
    }

    .radio-container {
      display: flex;
      padding: .5em;
    }

    label {
      margin: 0;
    }
  }

  .gender-hint {
    padding: 0 0 0 .5em;
    color: $red-2;
  }

  .terms-of-service-hint{
    color: $red-2;
  }

  .sign-up-error{
    padding: 1em 0;
    color: $red-2;
    font-size: 12px;
  }

  .country-placeholder {
    color: grey;
    padding-left: 5px;

    &.country-selected {
      color: $cyan-blue-3;
    }

    &.invalid {
      border-color: $red-2;
    }
  }


</style>
